import { FAMILIES } from "@/content/families"
import { colors } from "@/theme/colors"
import type { FamilyNumber } from "@/theme/colors"
import styles from "@/styles/components/CardFan.module.css"

/**
 * The active family covers, fanned in the order the Figma sign-in frame shows
 * them — not deal order, just a wide spread of colour behind whatever the host
 * screen wants noticed first.
 *
 * Retired families are filtered out so this stays in step with the deck: once
 * Monitors was retired, a hard-coded seven would show two greens (its green plus
 * Sees' new green) and one family too many.
 */
const RETIRED = new Set(FAMILIES.filter((family) => family.retired).map((family) => family.n))
const FAN: readonly FamilyNumber[] = ([1, 2, 4, 6, 7, 3, 5] as FamilyNumber[]).filter(
  (n) => !RETIRED.has(n)
)
const FAN_CENTER = (FAN.length - 1) / 2
/** How far the outer cards sit from centre, as % of the host's width. Tight
 *  enough that the (now six) larger cards overlap into a fanned hand rather than
 *  floating apart — the seven-card spread read as small and scattered. */
const FAN_SPREAD = 30
/** Degrees the outer cards tilt, relative to the upright centre card. */
const FAN_MAX_ANGLE = 20
/** How much higher the centre card sits than the two ends, in px. */
const FAN_ARCH = 70
/** Extra px the very first and last card drop below the circle, so they read
 *  as sitting below their neighbours rather than levelling out at 0 like the
 *  rest of the arc's foot. */
const FAN_END_DROP = 50
/** Extra px the three middle cards (the ones either side of centre, plus
 *  centre itself) are raised above the sqrt arc. A tilted card's top corner
 *  sits higher on screen than an upright one at the same lift, which is
 *  enough to level the middle three with the shallower-tilted pair beside
 *  them — this pushes them visibly past that, into their own raised group. */
const FAN_MIDDLE_LIFT = 24
/** Extra px the upright centre card is raised on top of `FAN_MIDDLE_LIFT`.
 *  It tilts least of the three, so a rotated neighbour's top corner still
 *  reads higher than its own at the same lift — this closes that gap so all
 *  three sit level instead of the centre dipping below its neighbours. */
const FAN_CENTER_LIFT = 15

export default function CardFan() {
  return (
    <div className={styles.root} aria-hidden="true">
      <div className={styles.backdrop} />
      <div className={styles.fan}>
        {FAN.map((family, index) => {
          const u = (index - FAN_CENTER) / FAN_CENTER
          const left = 50 + u * FAN_SPREAD
          const isEnd = index === 0 || index === FAN.length - 1
          const isMiddle = Math.abs(index - FAN_CENTER) <= 1
          const isCenter = index === FAN_CENTER
          const lift =
            FAN_ARCH * Math.sqrt(1 - u * u) -
            (isEnd ? FAN_END_DROP : 0) +
            (isMiddle ? FAN_MIDDLE_LIFT : 0) +
            (isCenter ? FAN_CENTER_LIFT : 0)
          return (
            <div
              key={family}
              className={styles.fanCard}
              style={
                {
                  left: `${left}%`,
                  "--fan-rotate": `${u * FAN_MAX_ANGLE}deg`,
                  "--fan-lift": `${lift}px`,
                  "--fan-z": FAN.length - Math.abs(index - FAN_CENTER),
                  backgroundColor: colors.family[family].cover,
                  backgroundImage: `url(/cards-web/c${family}-0.webp)`,
                } as React.CSSProperties
              }
            />
          )
        })}
      </div>
    </div>
  )
}
